"use client";

import Image from "next/image";
import Link from "next/link";
import { Fragment, useEffect, useRef, useState } from "react";
import { useTheme } from "next-themes";

import { Footer, Nav } from "@/app/_components/sections";
import {
  Code,
  IconArrowRight,
  IconCheck,
  IconCopy,
} from "@/app/_components/icons";
import { InstallSnippet } from "@/components/photon/install-snippet";
import { PhotonAtom } from "@/components/photon/photon-atom";
import {
  HARDWARE_GROUPS,
  MODEL_GROUPS,
  type CatalogChip,
} from "@/components/photon/photon-minimal-data";
import { ModalityInputs } from "@/components/photon/modality";
import { PhotonWordmark } from "@/components/photon/photon-wordmark";
import { pythonTokens } from "@/components/photon/python-tokens";

const BOOK_CALL_URL = "https://cal.com/jay-m87";
const DOCS_URL = "https://docs.moondream.ai/running-locally";

// Occasionally the atom emits a photon: a small light that rides a gentle
// sine wave from the nucleus toward the page edge, leaving a fading trail.
// Trajectories stay in the upper hemisphere so it never crosses the hero
// copy, and nothing fires under prefers-reduced-motion.
const SPARK_LENGTH = 1100;
const sparkWavePath = () => {
  // Mostly sideways, slightly upward (5°–30° above horizontal, either
  // direction): a long visible glide beside the atom, never over the copy.
  const elevation = 5 + Math.random() * 25;
  const degrees = Math.random() < 0.5 ? -elevation : -180 + elevation;
  const angle = (degrees * Math.PI) / 180;
  const amplitude = 5 + Math.random() * 3;
  const wavelength = 80 + Math.random() * 40;
  const phase = Math.random() * Math.PI * 2;
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);
  let d = "M 0 0";
  for (let t = 12; t <= SPARK_LENGTH; t += 12) {
    // Perpendicular sine offset, eased in so the light leaves the nucleus
    // cleanly before it starts to wave.
    const offset =
      amplitude *
      Math.sin((t / wavelength) * Math.PI * 2 + phase) *
      Math.min(1, t / 140);
    d += ` L ${(t * cos - offset * sin).toFixed(1)} ${(t * sin + offset * cos).toFixed(1)}`;
  }
  return d;
};

const PhotonSpark = () => {
  const [spark, setSpark] = useState<{ id: number; d: string } | null>(null);
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    let id = 0;
    let fireTimer: ReturnType<typeof setTimeout>;
    let clearTimer: ReturnType<typeof setTimeout>;
    const fire = () => {
      id += 1;
      setSpark({ id, d: sparkWavePath() });
      clearTimer = setTimeout(() => setSpark(null), 2100);
      fireTimer = setTimeout(fire, 5000 + Math.random() * 5000);
    };
    fireTimer = setTimeout(fire, 1400);
    return () => {
      clearTimeout(fireTimer);
      clearTimeout(clearTimer);
    };
  }, []);
  if (!spark) return null;
  return (
    <svg
      key={spark.id}
      className="photonm-spark"
      viewBox="-1100 -1100 2200 2200"
      aria-hidden="true"
    >
      <path className="photonm-spark-trail" d={spark.d} pathLength={1000} />
      <circle className="photonm-spark-head" r="2.5">
        <animateMotion dur="1.7s" fill="freeze" path={spark.d} />
      </circle>
    </svg>
  );
};

// Same investor set and assets as the About page.
const INVESTORS = [
  { src: "/images/investors/felicis.svg", name: "Felicis" },
  { src: "/images/investors/M12.svg", name: "M12, GitHub-Microsoft Fund" },
  { src: "/images/investors/ascend.png", name: "Ascend" },
] as const;

// Backtick spans in FAQ answers render as inline code and [text](href)
// spans as links; the JSON-LD keeps plain text.
const plainAnswer = (text: string) =>
  text.replaceAll("`", "").replace(/\[([^\]]+)\]\([^)]*\)/g, "$1");

const renderLinks = (text: string, key: number) => {
  // split with capture groups: [before, label, href, between, label, href, ...]
  const parts = text.split(/\[([^\]]+)\]\(([^)]+)\)/g);
  return (
    <Fragment key={key}>
      {parts.map((part, index) =>
        index % 3 === 1 ? (
          parts[index + 1].startsWith("http") ? (
            <a
              key={index}
              href={parts[index + 1]}
              target="_blank"
              rel="noreferrer"
              className="link"
            >
              {part}
            </a>
          ) : (
            <Link key={index} href={parts[index + 1]} className="link">
              {part}
            </Link>
          )
        ) : index % 3 === 2 ? null : (
          part
        ),
      )}
    </Fragment>
  );
};

const renderAnswer = (text: string) =>
  text
    .split("`")
    .map((part, index) =>
      index % 2 === 1 ? (
        <code className="mono" key={index}>
          {part}
        </code>
      ) : (
        renderLinks(part, index)
      ),
    );

const FAQ = [
  {
    q: "What is Photon?",
    a: "Moondream's inference engine for vision, audio, and language models. Each model is compiled into a single GPU program for its target chip. That compilation is where the speed comes from.",
  },
  {
    q: "What's different than vLLM or SGLang?",
    a: "vLLM and SGLang are built to maximize throughput over large request queues. Photon is built for realtime work: small batch counts, low latency, and steady tail latencies for systems that act on the next frame or the next utterance.",
  },
  {
    q: "Is it free?",
    a: "Yes. `pip install moondream` and run any supported model on your own hardware at no cost.",
  },
  {
    q: "Which models and hardware are supported?",
    a: "The [support grid](/photon/support) lists every model and chip Photon runs, and the validation status of each pair.",
  },
  {
    q: "How are the benchmark numbers produced?",
    a: "Every number on this site is generated from a published dataset: matched runs against reference engines. Explore the [performance benchmarks](/photon/performance).",
  },
  {
    q: "Can Photon run my production workload?",
    a: "That's what it's built for: realtime work like live video, robotics, and voice agents. [Book a call](https://cal.com/jay-m87) and we'll go through your models, chips, and latency targets.",
  },
] as const;

// ---- Family marks for the model chips (monochrome, track currentColor) ----
const QwenMark = () => (
  <svg viewBox="0 0 200 200" width="16" height="16" aria-hidden="true">
    <path
      fill="currentColor"
      d="M174.82 108.75L155.38 75L165.64 57.75C166.46 56.31 166.46 54.53 165.64 53.09L155.38 35.84C154.86 34.91 153.87 34.33 152.78 34.33H114.88L106.14 19.03C105.62 18.1 104.63 17.52 103.54 17.52H83.3C82.21 17.52 81.22 18.1 80.7 19.03L61.26 52.77H41.02C39.93 52.77 38.94 53.35 38.42 54.28L28.16 71.53C27.34 72.97 27.34 74.75 28.16 76.19L45.52 107.5L36.78 122.8C35.96 124.24 35.96 126.02 36.78 127.46L47.04 144.71C47.56 145.64 48.55 146.22 49.64 146.22H87.54L96.28 161.52C96.8 162.45 97.79 163.03 98.88 163.03H119.12C120.21 163.03 121.2 162.45 121.72 161.52L141.16 127.78H158.52C159.61 127.78 160.6 127.2 161.12 126.27L171.38 109.02C172.2 107.58 172.2 105.8 171.38 104.36L174.82 108.75Z"
    />
    <path fill="currentColor" d="M127.86 79.83H76.14L101.18 122.11L127.86 79.83Z" />
    <path
      fill="currentColor"
      opacity="0.45"
      d="M119.12 163.03H98.88L87.54 144.71H49.64L61.26 126.39H80.7L38.42 55.29H61.26L83.3 19.03L93.56 37.35L83.3 55.29H161.58L151.32 72.54L170.76 106.28H151.32L141.16 88.34L101.18 163.03H119.12Z"
    />
  </svg>
);

const GemmaMark = () => (
  <svg
    viewBox="0 0 24 24"
    width="16"
    height="16"
    fill="currentColor"
    fillRule="evenodd"
    aria-hidden="true"
  >
    <path d="M12.34 5.953a8.233 8.233 0 01-.247-1.125V3.72a8.25 8.25 0 015.562 2.232H12.34zm-.69 0c.113-.373.199-.755.257-1.145V3.72a8.25 8.25 0 00-5.562 2.232h5.304zm-5.433.187h5.373a7.98 7.98 0 01-.267.696 8.41 8.41 0 01-1.76 2.65L6.216 6.14zm-.264-.187H2.977v.187h2.915a8.436 8.436 0 00-2.357 5.767H0v.186h3.535a8.436 8.436 0 002.357 5.767H2.977v.186h2.976v2.977h.187v-2.915a8.436 8.436 0 005.767 2.357V24h.186v-3.535a8.436 8.436 0 005.767-2.357v2.915h.186v-2.977h2.977v-.186h-2.915a8.436 8.436 0 002.357-5.767H24v-.186h-3.535a8.436 8.436 0 00-2.357-5.767h2.915v-.187h-2.977V2.977h-.186v2.915a8.436 8.436 0 00-5.767-2.357V0h-.186v3.535A8.436 8.436 0 006.14 5.892V2.977h-.187v2.976zm6.14 14.326a8.25 8.25 0 005.562-2.233H12.34c-.108.367-.19.743-.247 1.126v1.107zm-.186-1.087a8.015 8.015 0 00-.258-1.146H6.345a8.25 8.25 0 005.562 2.233v-1.087zm-8.186-7.285h1.107a8.23 8.23 0 001.125-.247V6.345a8.25 8.25 0 00-2.232 5.562zm1.087.186H3.72a8.25 8.25 0 002.232 5.562v-5.304a8.012 8.012 0 00-1.145-.258zm15.47-.186a8.25 8.25 0 00-2.232-5.562v5.315c.367.108.743.19 1.126.247h1.107zm-1.086.186c-.39.058-.772.144-1.146.258v5.304a8.25 8.25 0 002.233-5.562h-1.087zm-1.332 5.69V12.41a7.97 7.97 0 00-.696.267 8.409 8.409 0 00-2.65 1.76l3.346 3.346zm0-6.18v-5.45l-.012-.013h-5.451c.076.235.162.468.26.696a8.698 8.698 0 001.819 2.688 8.698 8.698 0 002.688 1.82c.228.097.46.183.696.259zM6.14 17.848V12.41c.235.078.468.167.696.267a8.403 8.403 0 012.688 1.799 8.404 8.404 0 011.799 2.688c.1.228.19.46.267.696H6.152l-.012-.012zm0-6.245V6.326l3.29 3.29a8.716 8.716 0 01-2.594 1.728 8.14 8.14 0 01-.696.259zm6.257 6.257h5.277l-3.29-3.29a8.716 8.716 0 00-1.728 2.594 8.135 8.135 0 00-.259.696zm-2.347-7.81a9.435 9.435 0 01-2.88 1.96 9.14 9.14 0 012.88 1.94 9.14 9.14 0 011.94 2.88 9.435 9.435 0 011.96-2.88 9.14 9.14 0 012.88-1.94 9.435 9.435 0 01-2.88-1.96 9.434 9.434 0 01-1.96-2.88 9.14 9.14 0 01-1.94 2.88z" />
  </svg>
);

const OpenAIMark = () => (
  <svg
    viewBox="1.68 1.75 16.65 16.5"
    width="16"
    height="16"
    fill="currentColor"
    aria-hidden="true"
  >
    <path d="M11.248 18.25q-.825 0-1.568-.314a4.3 4.3 0 0 1-1.32-.874 4 4 0 0 1-1.304.214 4 4 0 0 1-2.046-.544 4.27 4.27 0 0 1-1.518-1.485 4 4 0 0 1-.56-2.095q0-.48.131-1.04A4.4 4.4 0 0 1 2.04 10.71a4.07 4.07 0 0 1 .017-3.4 4.2 4.2 0 0 1 1.056-1.418 3.8 3.8 0 0 1 1.6-.842 3.9 3.9 0 0 1 .76-1.683q.593-.759 1.451-1.188a4.04 4.04 0 0 1 1.832-.429q.825 0 1.567.313.742.314 1.32.875a4 4 0 0 1 1.304-.215q1.106 0 2.046.545a4.14 4.14 0 0 1 1.501 1.485q.578.941.578 2.095 0 .48-.132 1.04.66.61 1.023 1.419.363.792.363 1.666 0 .892-.38 1.717a4.3 4.3 0 0 1-1.072 1.435 3.8 3.8 0 0 1-1.584.825 3.8 3.8 0 0 1-.775 1.683 4.06 4.06 0 0 1-1.436 1.188 4.04 4.04 0 0 1-1.832.429m-4.076-2.062q.825 0 1.435-.347l3.103-1.782a.36.36 0 0 0 .164-.313v-1.42L7.881 14.62a.67.67 0 0 1-.726 0l-3.118-1.798a.5.5 0 0 1-.017.115v.198q0 .841.396 1.551.413.693 1.139 1.089a3.2 3.2 0 0 0 1.617.412m.165-2.69a.4.4 0 0 0 .181.05q.083 0 .165-.05l1.238-.71-3.977-2.31a.7.7 0 0 1-.363-.643v-3.58q-.825.362-1.32 1.122a2.9 2.9 0 0 0-.495 1.65q0 .809.413 1.55.412.743 1.072 1.123zm3.91 3.663q.875 0 1.585-.396a2.96 2.96 0 0 0 1.534-2.64v-3.564a.32.32 0 0 0-.165-.297l-1.254-.726v4.604a.7.7 0 0 1-.363.643l-3.119 1.799a3 3 0 0 0 1.783.577m.627-6.039V8.878L10.01 7.822 8.129 8.878v2.244l1.881 1.056zM7.057 5.859a.7.7 0 0 1 .363-.644l3.119-1.798a3 3 0 0 0-1.782-.578q-.874 0-1.584.396A2.96 2.96 0 0 0 6.05 4.324a3.07 3.07 0 0 0-.396 1.551v3.547q0 .199.165.314l1.237.726zm8.383 7.887q.825-.364 1.303-1.123.495-.758.495-1.65a3.15 3.15 0 0 0-.412-1.55q-.413-.743-1.073-1.123l-3.086-1.782q-.099-.065-.181-.049a.3.3 0 0 0-.165.05l-1.238.692 3.993 2.327a.6.6 0 0 1 .264.264.64.64 0 0 1 .1.363zm-3.317-8.382a.63.63 0 0 1 .726 0l3.135 1.831v-.297q0-.792-.396-1.501a2.86 2.86 0 0 0-1.105-1.155q-.71-.43-1.65-.43-.825 0-1.436.347L8.294 5.941a.36.36 0 0 0-.165.314v1.418z" />
  </svg>
);

const ChipMark = ({ mark }: { mark?: string }) => {
  if (mark === "moondream-vlm")
    return (
      <Image
        src="/images/photon/moondream-mark.svg"
        alt=""
        width={16}
        height={16}
        className="photonm-chip-mark"
      />
    );
  if (mark === "qwen-3" || mark === "qwen3-asr") return <QwenMark />;
  if (mark === "gemma") return <GemmaMark />;
  if (mark === "whisper") return <OpenAIMark />;
  return null;
};

const CatalogChipLink = ({ chip }: { chip: CatalogChip }) => (
  <Link href={chip.href} className="photonm-chip">
    {chip.newBadge ? (
      <span className="photonm-chip-new mono">{chip.newBadge}</span>
    ) : null}
    <ChipMark mark={chip.mark} />
    <span className="photonm-chip-name">{chip.name}</span>
    {chip.suffix ? (
      <span className="photonm-chip-suffix mono">{chip.suffix}</span>
    ) : null}
  </Link>
);

const CatalogColumn = ({
  title,
  groups,
  labelsAreModalities = false,
}: {
  title: string;
  groups: { label: string; chips: CatalogChip[] }[];
  labelsAreModalities?: boolean;
}) => (
  <div className="photonm-list">
    <div className="t-eyebrow photonm-list-title">{title}</div>
    {groups.map((group) => (
      <div key={group.label}>
        <div className="photonm-group-label mono">
          {labelsAreModalities ? (
            <ModalityInputs modality={group.label} size={14} />
          ) : (
            group.label
          )}
        </div>
        <div className="photonm-chips">
          {group.chips.map((chip) => (
            <CatalogChipLink key={chip.key} chip={chip} />
          ))}
        </div>
      </div>
    ))}
  </div>
);

// ---- Quickstart: real md.photon() API per docs.moondream.ai/running-locally
// (same surface as the family snippets in editorial.ts). ----
const CODE_TABS = [
  {
    id: "image",
    modality: "Image + text",
    code: `import moondream as md
from PIL import Image

model = md.photon("moondream3-preview")
answer = model.query(Image.open("aisle.jpg"), "Is the pallet blocking the aisle?")["answer"]
print(answer)`,
  },
  {
    id: "chat",
    modality: "Chat",
    code: `import moondream as md
from PIL import Image

model = md.photon("moondream3-preview")

# Multi-turn: append each reply, then ask the next question
messages = [
    {"role": "system", "content": "You are a concise warehouse operations assistant."},
    {"role": "user", "content": "Draft a one-line end-of-shift status update."},
]
reply = model.chat(messages)["message"]
print(reply["content"])

messages += [reply, {"role": "user", "content": "Now expand it to a bullet list."}]
reply = model.chat(messages)["message"]
print(reply["content"])

# Images drop into any turn, OpenAI-style
image_url = model.encode_image(Image.open("aisle.jpg")).image_url
messages += [reply, {
    "role": "user",
    "content": [
        {"type": "image_url", "image_url": {"url": image_url}},
        {"type": "text", "text": "Does this aisle match the update?"},
    ],
}]
print(model.chat(messages)["message"]["content"])`,
  },
  {
    id: "audio",
    modality: "Audio",
    code: `import moondream as md

model = md.photon("whisper-large-v3-turbo")
result = model.transcribe("meeting.m4a", timestamps="word")
print(result["text"])`,
  },
] as const;

const QuickstartCode = () => {
  const [tab, setTab] = useState<(typeof CODE_TABS)[number]["id"]>("image");
  const [copied, setCopied] = useState(false);
  const copyTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => () => clearTimeout(copyTimer.current), []);

  const active = CODE_TABS.find((t) => t.id === tab) ?? CODE_TABS[0];

  const onCopy = () => {
    if (typeof navigator !== "undefined" && navigator.clipboard) {
      navigator.clipboard.writeText(active.code).catch(() => {});
    }
    setCopied(true);
    clearTimeout(copyTimer.current);
    copyTimer.current = setTimeout(() => setCopied(false), 1600);
  };

  return (
    <div className="photon-code-wrap photonm-code-wrap">
      <div className="photon-code-head">
        <div>
          <div className="t-eyebrow" style={{ color: "var(--text-tertiary)" }}>
            Quickstart
          </div>
          <div className="t-heading-md" style={{ marginTop: 4 }}>
            Sample code.
          </div>
        </div>
        <div className="seg" role="group" aria-label="Input type">
          {CODE_TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              className={tab === t.id ? "is-active" : ""}
              onClick={() => setTab(t.id)}
            >
              <ModalityInputs modality={t.modality} size={14} />
            </button>
          ))}
        </div>
      </div>
      <div className="photonm-code-body">
        <button
          type="button"
          className={`photonm-code-copy ${copied ? "is-copied" : ""}`}
          aria-label="Copy code"
          onClick={onCopy}
        >
          {copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
        </button>
        <Code lang="python" tokens={pythonTokens(active.code)} />
      </div>
      <p
        className="t-caption mono"
        style={{ color: "var(--text-tertiary)", marginTop: 12 }}
      >
        <a href={DOCS_URL} target="_blank" rel="noreferrer" className="link">
          API reference
        </a>{" "}
        · docs.moondream.ai/running-locally
      </p>
    </div>
  );
};

export function PhotonMinimalPage({ headline }: { headline: string }) {
  const { resolvedTheme, setTheme } = useTheme();
  const [mounted, setMounted] = useState(false);

  useEffect(() => setMounted(true), []);

  const theme: "dark" | "light" =
    mounted && resolvedTheme === "dark" ? "dark" : "light";

  const toggleTheme = () => setTheme(theme === "dark" ? "light" : "dark");

  return (
    <div className="md-landing">
      <Nav theme={theme} onToggleTheme={toggleTheme} />

      <main>
        {/* The hero band stays on the dark canvas in both themes - the WebGL
            atom is tuned for it, matching the always-dark video hero the
            page shipped with before. */}
        <section className="photonm-hero">
          <div className="container photonm-hero-inner">
            <div className="hero-topline">
              <Link
                href="/blog/photon-2-1-speech-recognition"
                className="hero-announce"
              >
                <span className="hero-announce-tag">New</span>
                <span className="hero-announce-text">
                  Photon 2.1: speech recognition, up to 3.1× faster
                </span>
                <span className="hero-announce-arrow">
                  <IconArrowRight size={14} />
                </span>
              </Link>
            </div>
            <div className="photonm-hero-visual">
              <PhotonAtom />
              <PhotonSpark />
            </div>
            <PhotonWordmark className="photonm-wordmark" />
            <h1 className="t-display-md photonm-h1">
              The world&apos;s fastest multimodal inference engine.
            </h1>
            <p className="photonm-sub">
              <span className="photonm-sub-line">
                {headline}
              </span>
              <br />
              Powered by compiled megakernels. Free.
            </p>
            <Link
              href="/photon/performance"
              className="family-cta photonm-proof-link"
            >
              Benchmarks <IconArrowRight size={13} />
            </Link>
            <div className="photonm-cta-row">
              <InstallSnippet />
              <a
                href={BOOK_CALL_URL}
                target="_blank"
                rel="noreferrer"
                className="btn btn-secondary"
              >
                Book a call
              </a>
            </div>
          </div>
        </section>

        {/* Supported models and hardware, generated from the release manifest */}
        <section className="photonm-catalog" id="models">
          <div className="container photonm-catalog-grid">
            <CatalogColumn
              title="Models"
              groups={MODEL_GROUPS}
              labelsAreModalities
            />
            <CatalogColumn title="Hardware" groups={HARDWARE_GROUPS} />
            <Link
              href="/photon/support"
              className="family-cta photonm-catalog-support"
            >
              View full support matrix <IconArrowRight size={13} />
            </Link>
          </div>
        </section>

        <section className="photonm-quickstart">
          <div className="container">
            <QuickstartCode />
          </div>
        </section>

        {/* Social proof: same investors and assets as the About page */}
        <section className="photonm-backed">
          <div className="container">
            <div className="t-eyebrow" style={{ color: "var(--text-tertiary)" }}>
              Backed by
            </div>
            <div className="photonm-backers">
              {INVESTORS.map((investor) => (
                <span className="photonm-backer" key={investor.name}>
                  <Image
                    src={investor.src}
                    alt={`${investor.name} logo`}
                    width={120}
                    height={32}
                  />
                  <span>{investor.name}</span>
                </span>
              ))}
            </div>
          </div>
        </section>

        <section className="photonm-faq" id="faq">
          <div className="container">
            <div className="t-eyebrow" style={{ color: "var(--text-tertiary)" }}>
              FAQ
            </div>
            <div className="photonm-faq-list">
              {FAQ.map((item) => (
                <details className="photonm-faq-item" key={item.q}>
                  <summary>{item.q}</summary>
                  <p>{renderAnswer(item.a)}</p>
                </details>
              ))}
            </div>
            <script
              type="application/ld+json"
              dangerouslySetInnerHTML={{
                __html: JSON.stringify({
                  "@context": "https://schema.org",
                  "@type": "FAQPage",
                  mainEntity: FAQ.map((item) => ({
                    "@type": "Question",
                    name: item.q,
                    acceptedAnswer: {
                      "@type": "Answer",
                      text: plainAnswer(item.a),
                    },
                  })),
                }),
              }}
            />
            <nav className="photonm-quicklinks mono" aria-label="Photon pages">
              <Link href="/photon/performance">Benchmarks</Link>
              <Link href="/photon/updates">Changelog</Link>
              <Link href="/photon/support">Support</Link>
              <a href={DOCS_URL} target="_blank" rel="noreferrer">
                Docs
              </a>
            </nav>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
